import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuOption, NonGroupOption } from '../types';
import { CONTEXT_MENU_CONFIG } from '../components/title-bar/title-context-menu/menu_config';

/**
 * Event emitted when user changes a menu value (checkbox, radio, etc.)
 */
export interface MenuValueChangeEvent {
  field: string;
  value: any;
  timestamp: number;
}

declare global {
  interface Window {
    updateMenuConfig: (groupLabel: string, field: string, updates: any) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class MenuConfigService {
  // Configuration stream (for SDK updates and initial config)
  private menuConfigSource = new BehaviorSubject<MenuOption[]>(
    JSON.parse(JSON.stringify(CONTEXT_MENU_CONFIG)) // Deep clone to avoid mutations
  );
  public menuConfig$: Observable<MenuOption[]> = this.menuConfigSource.asObservable();

  // User interaction stream (for catching checkbox/radio clicks)
  private userValueChangedSource = new Subject<MenuValueChangeEvent>();
  public userValueChanged$: Observable<MenuValueChangeEvent> = this.userValueChangedSource.asObservable();

  constructor() {
    // Expose API to window for SDK integration
    if (typeof window !== 'undefined') {
      window.updateMenuConfig = this.updateMenuOption.bind(this);
    }
  }

  /**
   * Get current configuration snapshot
   */
  getCurrentConfig(): MenuOption[] {
    return this.menuConfigSource.value;
  }

  /**
   * Update entire configuration (full replacement)
   * @param newConfig - New menu configuration array
   */
  updateConfig(newConfig: MenuOption[]): void {
    const clonedConfig = JSON.parse(JSON.stringify(newConfig));
    this.menuConfigSource.next(clonedConfig);
  }

  /**
   * Update a specific menu option by group label and field
   * @param groupLabel - Label of the group containing the option
   * @param field - Field name of the option to update
   * @param updates - Partial updates to apply to the option
   */
  updateMenuOption(groupLabel: string, field: string, updates: Partial<NonGroupOption>): void {
    const currentConfig = this.menuConfigSource.value;
    const newConfig = JSON.parse(JSON.stringify(currentConfig)); // Deep clone

    // Find the group
    const group = newConfig.find(option =>
     option.label === groupLabel &&
     (option.type === 'group' || option.type === 'group-mode-checkbox')
    );

if (!group) {
  console.warn(`MenuConfigService: Group "${groupLabel}" not found`);
  return;
}

    // Find the field within the group
    const targetOption = group.options?.find(
      opt => opt.type !== 'group' && (opt as NonGroupOption).field === field
    ) as NonGroupOption | undefined;

    if (!targetOption) {
      console.warn(`MenuConfigService: Field "${field}" not found in group "${groupLabel}"`);
      return;
    }

    // Apply updates
    Object.assign(targetOption, updates);

    // Emit updated config
    this.menuConfigSource.next(newConfig);
  }

  /**
   * Update the values array for a specific field
   * @param groupLabel - Label of the group containing the option
   * @param field - Field name of the option
   * @param newValues - New array of values
   */
  updateMenuValues(groupLabel: string, field: string, newValues: any[]): void {
    this.updateMenuOption(groupLabel, field, { values: newValues });
  }

  /**
   * Update the defaultValue for a specific field
   * @param groupLabel - Label of the group containing the option
   * @param field - Field name of the option
   * @param newValue - New default value (can be single value or array)
   */
  updateDefaultValue(groupLabel: string, field: string, newValue: any): void {
    this.updateMenuOption(groupLabel, field, { defaultValue: newValue });
  }

  /**
   * Reset configuration to original static config
   */
  resetToDefaults(): void {
    const originalConfig = JSON.parse(JSON.stringify(CONTEXT_MENU_CONFIG));
    this.menuConfigSource.next(originalConfig);
  }

  /**
   * Called when user changes a menu value (checkbox, radio, etc.)
   * Emits event that can be subscribed to for triggering other updates
   *
   * @param field - Field name that changed
   * @param value - New value
   *
   * @example
   * // This is called automatically by ContextMenuHelperService
   * // when user interacts with the menu
   */
  onUserValueChange(field: string, value: any): void {
    this.userValueChangedSource.next({
      field,
      value,
      timestamp: Date.now()
    });
  }

  /**
   * Helper method to subscribe to specific field changes
   * Returns a subscription that must be unsubscribed when done
   *
   * @param field - Field name to listen for
   * @param handler - Callback function to execute when field changes
   * @returns Subscription that should be stored and unsubscribed in ngOnDestroy
   *
   * @example
   * // Register a handler for when user enables ICAO checks
   * this.subscriptions.push(
   *   this.menuConfigService.onFieldChange('enable_icao_checks', (enabled) => {
   *     if (enabled) {
   *       this.menuConfigService.updateDefaultValue(
   *         'ICAO PreChecks',
   *         'icao_threshold',
   *         [PrecaptureChecks.GLASSES, PrecaptureChecks.FACE_ANGLE]
   *       );
   *     }
   *   })
   * );
   */
  onFieldChange(field: string, handler: (value: any) => void): Subscription {
    return this.userValueChanged$
      .pipe(filter(event => event.field === field))
      .subscribe(event => handler(event.value));
  }

  /**
   * Helper method to find and update a nested option (supports deeper nesting if needed)
   * @param config - Configuration array to search
   * @param groupLabel - Group label
   * @param field - Field name
   * @param updates - Updates to apply
   * @returns true if found and updated, false otherwise
   */
  private findAndUpdateOption(
    config: MenuOption[],
    groupLabel: string,
    field: string,
    updates: Partial<NonGroupOption>
  ): boolean {
    for (const option of config) {
      if (option.label === groupLabel && option.type === 'group') {
        const targetOption = option.options?.find(
          opt => opt.type !== 'group' && (opt as NonGroupOption).field === field
        ) as NonGroupOption | undefined;

        if (targetOption) {
          Object.assign(targetOption, updates);
          return true;
        }
      }

      // Recursively search nested groups
      if (option.type === 'group' && option.options) {
        if (this.findAndUpdateOption(option.options, groupLabel, field, updates)) {
          return true;
        }
      }
    }

    return false;
  }
}
